import React, { useState, useEffect, useMemo } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Button, Form, Table, Pagination } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from './Breadcrumb.js';
import CityGeneralInfo from './CityGeneralInfo.js';
import NavTabs from './NavTabs';
import { TicketCountContext } from './TicketCountContext';
import { exportCityPdf } from './api/exportCityPdf.js';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const FIELD_LABELS = {
    name: 'City Name',
    continent: 'Continent',
    country: 'Country',
    cityId: 'City ID',
    url: 'Regional URL',
    citizensNumber: 'City Citizens Number',
    regionCitizensNumber: 'Region Citizens Number',
    languagesSpoken: 'Languages Spoken',
    cityRegionDescription: 'City Region Description',
    officeUrl: 'Citizen Office Desk URL',
    address: 'Address',
    phone: 'Phone',
    workingHours: 'Working Hours',
    govUrl: 'Government URL',
    latitude: 'Latitude',
    longitude: 'Longitude',
    activityStatus: 'Activity Status',
    currency: 'Currency',
    legalUrl: 'Legal URL',
    remarks: 'Remarks',
    studentJobs: 'Student Jobs Salary Range',
    internJobs: 'Intern Jobs Salary Range',
    oneThreeJobs: '1-3 Years Jobs Salary Range',
    threeJobs: '3+ Years Jobs Salary Range',
    uplift: 'Uplift',
    service: 'Service',
    providerUrl: 'Provider URL',
    description: 'Description',
    googleRating: 'Google Rating',
    showStatus: 'Show Status',
    telephone: 'Telephone',
    classification: 'Classification',
    studentsNumber: 'Students Number',
    cityInsights: 'City Insights',
    universities: 'Universities',
    'cityInsight.service': 'City Insight Service',
    'cityInsight.name': 'City Insight Name',
    'cityInsight.url': 'City Insight URL',
    'cityInsight.providerUrl': 'City Insight Provider URL',
    'cityInsight.description': 'City Insight Description',
    'cityInsight.googleRating': 'City Insight Google Rating',
    'cityInsight.showStatus': 'City Insight Show Status',
};

const formatFieldLabel = (field) => {
    if (!field) return '—';
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];
    const parts = field.split('.');
    if (parts.length > 1) {
        const last = parts[parts.length - 1];
        const label = FIELD_LABELS[last] || last;
        return `${parts.slice(0, -1).join(' / ')} - ${label}`;
    }
    return field;
};

const humanizeKey = (key) => {
    if (FIELD_LABELS[key]) return FIELD_LABELS[key];
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
};

const formatObjectEntry = (obj) => {
    return Object.entries(obj)
        .filter(([k, v]) => k !== '_id' && v !== '' && v !== null && v !== undefined)
        .map(([k, v]) => `${humanizeKey(k)}: ${v}`)
        .join(', ');
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value !== 'string') return String(value);
    const trimmed = value.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                if (parsed.length === 0) return '—';
                if (typeof parsed[0] === 'object' && parsed[0] !== null) {
                    const entries = parsed.map((item) => formatObjectEntry(item)).filter(Boolean);
                    return entries.length ? entries.join('\n') : '—';
                }
                return parsed.join(', ');
            }
            if (typeof parsed === 'object' && parsed !== null) {
                return formatObjectEntry(parsed) || '—';
            }
        } catch (e) {
            // not JSON, fall through and display as-is
        }
    }
    return value;
};

const CityEditHistory = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { ticketCount } = React.useContext(TicketCountContext);
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        name: '',
        country: {},
        url: '',
    });

    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const userNavItems = [
        { to: '/city-edit/:id', eventKey: 'cityInfo', label: 'About' },
        { to: '/city-edit-companies/:id', eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`/city-edit-jobs/${id}`] },
        { to: '/city-edit-salary/:id', eventKey: 'salariesInfo', label: 'Average salaries' },
        { to: '/city-edit-insights/:id', eventKey: 'cityInsights', label: 'City insights' },
        { to: '/city-edit-universities/:id', eventKey: 'universities', label: 'Universities' },
        { to: '#', eventKey: 'cityComments', label: 'Comments' },
        { to: '/city-edit-history/:id', eventKey: 'history', label: 'History of changes' },
    ];

    useEffect(() => {
        const fetchCity = async () => {
            try {
                const res = await api.get(`/api/cities/${id}`);
                const data = res?.data || {};
                setFormData({
                    name: data.name || '',
                    country: (data.country && typeof data.country === 'object') ? data.country : {},
                    url: data.url || '',
                });
            } catch (error) {
                console.error('Error fetching city:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchCity();
    }, [id]);

    useEffect(() => {
        const fetchHistory = async () => {
            setIsLoadingHistory(true);
            try {
                const res = await api.get(`/api/cities/${id}/history`);
                setHistory(res.data || []);
            } catch (err) {
                console.error('Error fetching city history:', err);
            } finally {
                setIsLoadingHistory(false);
            }
        };
        fetchHistory();
    }, [id]);

    const handleExportPdf = async () => {
        try {
            await exportCityPdf(id);
        } catch (error) {
            console.error('Error exporting city PDF:', error);
            alert('An error occurred while exporting the city PDF.');
        }
    };

    const flatRows = useMemo(() => {
        const rows = [];
        history.forEach((entry) => {
            const changedBy = entry.changedBy;
            const name = changedBy
                ? `${changedBy.firstName || ''} ${changedBy.familyName || ''}`.trim() || '—'
                : '—';
            const changes = entry.changes?.length ? entry.changes : [{ field: '', oldValue: '', newValue: '' }];
            changes.forEach((c) => {
                rows.push({ entryId: entry._id, date: entry.createdAt, section: entry.section, field: c.field, oldValue: c.oldValue, newValue: c.newValue, name });
            });
        });
        return rows;
    }, [history]);

    const totalPages = Math.ceil(flatRows.length / rowsPerPage);
    const currentRows = flatRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const getPaginationItems = () => {
        if (totalPages <= 1) return [1];
        const range = (s, e) => { const o = []; for (let i = s; i <= e; i++) o.push(i); return o; };
        const boundary = 1, sibling = 2;
        const startPages = range(1, Math.min(boundary, totalPages));
        const endPages = range(Math.max(totalPages - boundary + 1, boundary + 1), totalPages);
        const siblingsStart = Math.max(Math.min(currentPage - sibling, totalPages - boundary - sibling * 2 - 1), boundary + 2);
        const siblingsEnd = Math.min(Math.max(currentPage + sibling, boundary + sibling * 2 + 2), totalPages - boundary - 1);
        const items = [
            ...startPages,
            ...(siblingsStart > boundary + 2 ? ['ellipsis'] : boundary + 1 < totalPages - boundary ? [boundary + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundary - 1 ? ['ellipsis'] : totalPages - boundary > boundary ? [totalPages - boundary] : []),
            ...endPages,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    return (
        <>
            <Sidebar ticketCount={ticketCount} />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={formData.name} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <CityGeneralInfo formData={formData} />
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={userNavItems} />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <h5 className="mb-4">History of changes</h5>
                                                        {isLoadingHistory ? (
                                                            <p>Loading history...</p>
                                                        ) : flatRows.length === 0 ? (
                                                            <p className="text-muted">No changes recorded yet.</p>
                                                        ) : (
                                                            <>
                                                                <Table hover responsive>
                                                                    <thead>
                                                                        <tr>
                                                                            <th className="text-nowrap">#</th>
                                                                            <th className="text-nowrap">Date & Time</th>
                                                                            <th className="text-nowrap">Section</th>
                                                                            <th className="text-nowrap">Field</th>
                                                                            <th>Old Value</th>
                                                                            <th>New Value</th>
                                                                            <th className="text-nowrap">Changed by</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {currentRows.map((row, idx) => (
                                                                            <tr key={`${row.entryId}-${idx}`}>
                                                                                <td className="text-nowrap">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                                                                                <td className="text-nowrap">{formatDate(row.date)}</td>
                                                                                <td className="text-nowrap">{row.section}</td>
                                                                                <td className="text-nowrap">{formatFieldLabel(row.field)}</td>
                                                                                <td style={{ whiteSpace: 'pre-wrap', minWidth: '200px' }}>{formatValue(row.oldValue)}</td>
                                                                                <td style={{ whiteSpace: 'pre-wrap', minWidth: '200px' }}>{formatValue(row.newValue)}</td>
                                                                                <td className="text-nowrap">{row.name}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                                <Row className="pagination-wrapper mt-4">
                                                                    <Col className="d-flex align-items-center">
                                                                        <span>Total {flatRows.length} {flatRows.length === 1 ? 'change' : 'changes'}</span>
                                                                        <Pagination>
                                                                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                                                                                <BsChevronLeft />
                                                                            </Pagination.Prev>
                                                                            {getPaginationItems().map((item, i) =>
                                                                                item === 'ellipsis'
                                                                                    ? <Pagination.Ellipsis key={`e-${i}`} disabled />
                                                                                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => setCurrentPage(item)}>{item}</Pagination.Item>
                                                                            )}
                                                                            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0}>
                                                                                <BsChevronRight />
                                                                            </Pagination.Next>
                                                                        </Pagination>
                                                                    </Col>
                                                                    <Col className="pagination-controls">
                                                                        <div className="pagination-controls-per-page">
                                                                            <Form.Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                                                <option value="10">10 / page</option>
                                                                                <option value="20">20 / page</option>
                                                                                <option value="40">40 / page</option>
                                                                            </Form.Select>
                                                                        </div>
                                                                        <label className="pagination-controls-goto-label" htmlFor="history-goto-page">Go to</label>
                                                                        <Form.Control
                                                                            id="history-goto-page"
                                                                            className="pagination-controls-goto-input"
                                                                            type="number"
                                                                            value={currentPage}
                                                                            onChange={(e) => { const p = Number(e.target.value); if (p >= 1 && p <= totalPages) setCurrentPage(p); }}
                                                                            onBlur={() => { if (currentPage < 1) setCurrentPage(1); if (currentPage > totalPages) setCurrentPage(totalPages); }}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            </>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button variant="outline-secondary" className="ms-3" onClick={() => navigate('/onboarding-city-list')}>
                                Cancel
                            </Button>
                            <Button variant="outline-secondary" onClick={handleExportPdf}>
                                Download city report
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </main>
        </>
    );
};

export default CityEditHistory;
